import React, { useRef } from "react";
import { motion } from "framer-motion";
import { Building2, BriefcaseBusiness, Sofa, Layers, Wrench, Hammer } from "lucide-react";
import { useGSAP } from "@gsap/react";
import gsap from "gsap";

const services = [
  {
    title: "Architectural Design",
    description:
      "We create innovative, functional and aesthetically pleasing architectural designs tailored to meet client needs while ensuring efficiency and sustainability.",
    icon: Building2,
  },
  {
    title: "Project Execution",
    description:
      "From groundbreaking to project completion, we ensure seamless execution with strict adherence to quality, timelines, and budget constraints.",
    icon: Hammer,
  },
  {
    title: "Consultancy Services",
    description:
      "We offer expert guidance in project planning, feasibility studies, cost estimation, and supervision to ensure smooth project implementation and cost efficiency.",
    icon: BriefcaseBusiness,
  },
  {
    title: "Interior Design",
    description:
      "We transform spaces with creative and practical interior designs that enhance functionality, comfort, and aesthetics for residential and commercial properties.",
    icon: Sofa,
  },
  {
    title: "Structural Design",
    description:
      "Our structural engineering solutions focus on safety, durability, and efficiency, ensuring that buildings are designed to withstand various loads and conditions.",
    icon: Layers,
  },
  {
    title: "Maintenance Services",
    description:
      "We provide ongoing maintenance and repair solutions to ensure the longevity and performance of buildings, minimizing future costs and risks.",
    icon: Wrench,
  },
];

const ServicesPremium = () => {
  const sectionRef = useRef(null);

  useGSAP(
    () => {
      gsap.from(".service-card", {
        y: 60,
        opacity: 0,
        stagger: 0.14,
        duration: 0.75,
        ease: "power3.out",
        scrollTrigger: {
          trigger: sectionRef.current,
          start: "top 72%",
        },
      });
    },
    { scope: sectionRef }
  );

  return (
    <section id="services" ref={sectionRef} className="relative overflow-hidden py-24 sm:py-28 bg-slate-50">
      <div className="absolute inset-0 bg-slate-50" aria-hidden="true" />
      <div className="pointer-events-none absolute inset-0 opacity-10" aria-hidden="true" />
      <div className="pointer-events-none absolute inset-0 opacity-35" aria-hidden="true">
        <div className="decor-float absolute right-10 top-8 text-[110px] font-black leading-none text-transparent [background:linear-gradient(135deg,rgba(56,189,248,0.22),rgba(30,64,175,0.06))] bg-clip-text">
          30m
        </div>
        <div className="decor-float absolute left-8 bottom-6 text-[92px] font-black leading-none text-transparent [background:linear-gradient(135deg,rgba(14,165,233,0.2),rgba(37,99,235,0.05))] bg-clip-text [animation-delay:0.9s]">
          45m
        </div>
      </div>
      <div className="absolute inset-0 bg-gradient-to-br from-slate-100/10 via-transparent to-slate-100/10" aria-hidden="true" />
      <div className="section-shell relative z-10">
        <div className="section-title">
          <p className="section-kicker !text-black">Core Services</p>
          <h2 className="section-heading !text-black">Integrated capabilities for complex developments</h2>
          <p className="section-subtitle !text-black">
            Specialized expertise delivered through a coordinated multidisciplinary team.
          </p>
        </div>

        <div className="mt-12 grid gap-6 sm:grid-cols-2 xl:grid-cols-3">
          {services.map((service) => {
            const Icon = service.icon;
            return (
              <motion.article
                key={service.title}
                whileHover={{ scale: 1.05, y: -6 }}
                transition={{ type: "spring", stiffness: 220, damping: 18 }}
                className="service-card premium-card p-6"
              >
                <div className="inline-flex rounded-xl bg-amberSoft/20 p-3 text-amberSoft">
                  <Icon size={22} />
                </div>
                <h3 className="mt-5 text-lg font-semibold !text-black">{service.title}</h3>
                <p className="mt-3 text-sm leading-relaxed !text-black">{service.description}</p>
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default ServicesPremium;
